// =========================================================================
// Cobranza Stats — derivación de KPIs de cobranza y comisión por aseguradora
// =========================================================================
//
// Utility puro que toma los `widgetSummaries` (Map<insurerCode, summary>) que
// ya alimenta el grid del dashboard y deriva los KPIs principales del tab
// Analítica Avanzada:
//
//   - Emitidas:  ISSUED + PAYMENT_RECORDED + COMPLETED
//                (cumulativo, todas las pólizas que pasaron por la aseguradora)
//   - Pagadas:   (PAYMENT_RECORDED + COMPLETED) − Pagadas con error de notif
//                (las saneadas: pago registrado Y notificado a la aseguradora
//                 con éxito. Esta es la cobranza "limpia".)
//   - Pagadas con error de notif:
//                Subset de las con pago donde la notificación a SM/SC falló.
//                Count viene del endpoint /policies/payment-errors, no del
//                statusDistribution. Se pasa como parámetro al utility para
//                mantenerlo puro.
//   - % Cobranza: Pagadas saneadas / Emitidas * 100  (NaN si Emitidas == 0)
//   - Pendientes: Emitidas − (PAYMENT_RECORDED + COMPLETED)
//                (los que ni siquiera registraron pago. Los con error sí
//                 tienen pago — van en su propio bucket, no en Pendientes.)
//
// Identidad: Pagadas + PagadasConError + Pendientes ≡ Emitidas
//
// NO requiere llamadas al backend desde este archivo — la data viene de:
//   - statusDistribution del widget summary (emitidas, total con pago).
//   - Count externo de errors de notif que el caller pasa como parámetro.
//
// **Pendiente**: cuando el backend exponga agregados de monto (premium total,
// brokerage %), extender este archivo con `deriveCommissionAmount(...)`.

import { InsurerWidgetSummary } from '../models/seguros.interface';

const EMITIDAS_STATUSES = new Set<string>(['ISSUED', 'PAYMENT_RECORDED', 'COMPLETED']);
const PAGADAS_STATUSES = new Set<string>(['PAYMENT_RECORDED', 'COMPLETED']);

export interface CobranzaStats {
  emitidas: number;
  /** Pagadas saneadas: total con pago menos las con error de notificación. */
  pagadas: number;
  /** Subset de "con pago" cuya notificación a la aseguradora falló. */
  pagadasConError: number;
  /** Emitidas que ni siquiera registraron pago (sin error notif). */
  pendientes: number;
  /** Tasa 0-100 sobre pagadas saneadas. NaN cuando Emitidas == 0. */
  tasaCobranza: number;
}

export interface InsurerCobranzaRow {
  insurerCode: string;
  emitidas: number;
  pagadas: number;
  pagadasConError: number;
  tasaCobranza: number;
}

/**
 * Deriva las stats de cobranza para una sola aseguradora.
 *
 * Fuente de verdad para el split saneadas vs con error: el campo
 * `breakdown.by_notification` que devuelve el endpoint status-distribution
 * (cuenta el flag `payment_notification_error: true`). Esto garantiza
 * coherencia con los splits por frecuencia (`cross_frequency_notification`)
 * que también usan ese mismo flag.
 *
 * @param summary Widget summary con statusDistribution.
 * @param paymentNotifErrorsCount [DEPRECATED] Antes se usaba como
 *   complemento del endpoint /policies/payment-errors. Hoy solo se usa
 *   como FALLBACK cuando el breakdown no está disponible (clientes viejos).
 *   Mantener firma compatible para no romper consumidores.
 */
export function deriveCobranzaFromSummary(
  summary: InsurerWidgetSummary | null | undefined,
  paymentNotifErrorsCount: number = 0
): CobranzaStats {
  const dist = summary?.statusDistribution?.distribution || [];
  let emitidas = 0;
  let pagadasRaw = 0;
  let pagadasConErrorFromBreakdown = 0;
  let breakdownAvailable = false;

  for (const item of dist) {
    const status = (item?.status || '').toString();
    const count = item?.count || 0;
    if (EMITIDAS_STATUSES.has(status)) {
      emitidas += count;
    }
    if (PAGADAS_STATUSES.has(status)) {
      pagadasRaw += count;
      const byNotif = item?.breakdown?.by_notification;
      if (byNotif && byNotif.length > 0) {
        breakdownAvailable = true;
        const errEntry = byNotif.find(b => b.key === 'notification_error');
        pagadasConErrorFromBreakdown += errEntry?.count || 0;
      }
    }
  }

  // Si el breakdown está disponible (backend actualizado), úsalo. Si no,
  // caer al param legacy paymentNotifErrorsCount.
  const pagadasConErrorRaw = breakdownAvailable
    ? pagadasConErrorFromBreakdown
    : paymentNotifErrorsCount;

  // Defensivo: cappear para no producir negativos.
  const pagadasConError = Math.min(Math.max(0, pagadasConErrorRaw), pagadasRaw);
  const pagadas = pagadasRaw - pagadasConError;
  // Pendientes = los que ni pago registraron. Las con error SÍ tienen pago,
  // se cuentan aparte. Por eso restamos pagadasRaw (no pagadas saneadas).
  const pendientes = Math.max(0, emitidas - pagadasRaw);
  const tasaCobranza = emitidas > 0 ? (pagadas / emitidas) * 100 : NaN;
  return { emitidas, pagadas, pagadasConError, pendientes, tasaCobranza };
}

/**
 * Agrega las stats globales sumando los splits per-insurer. Si se pasa el Map
 * de errors counts, cada aseguradora aporta su propio split. Si no, todas las
 * con pago se cuentan como Pagadas (compatibilidad hacia atrás).
 *
 * @param visibleInsurerCodes Si se pasa, solo se suman las aseguradoras cuyo
 *   code esté en este array. Defensa en profundidad: aunque el componente
 *   debería invalidar el Map al cambiar de filtro, este whitelist evita
 *   regresiones si alguien lo olvida y entradas obsoletas sobreviven.
 */
export function deriveGlobalCobranza(
  summaries: Map<string, InsurerWidgetSummary>,
  paymentNotifErrorsCountByInsurer?: Map<string, number>,
  visibleInsurerCodes?: string[]
): CobranzaStats {
  let emitidas = 0;
  let pagadas = 0;
  let pagadasConError = 0;
  const allowed = visibleInsurerCodes && visibleInsurerCodes.length > 0
    ? new Set(visibleInsurerCodes) : null;
  summaries.forEach((summary, code) => {
    if (allowed && !allowed.has(code)) return;
    const errCount = paymentNotifErrorsCountByInsurer?.get(code) || 0;
    const stats = deriveCobranzaFromSummary(summary, errCount);
    emitidas += stats.emitidas;
    pagadas += stats.pagadas;
    pagadasConError += stats.pagadasConError;
  });
  // Pendientes globales: emitidas − (saneadas + con error) = emitidas − totalConPago.
  const pendientes = Math.max(0, emitidas - pagadas - pagadasConError);
  const tasaCobranza = emitidas > 0 ? (pagadas / emitidas) * 100 : NaN;
  return { emitidas, pagadas, pagadasConError, pendientes, tasaCobranza };
}

/**
 * Devuelve un row por aseguradora del listado, ordenado por tasa de cobranza
 * descendente (la mejor primero). Aseguradoras sin summary o sin emisiones
 * quedan al final. Cada row aplica su propio split usando el Map opcional.
 */
export function deriveCobranzaByInsurer(
  summaries: Map<string, InsurerWidgetSummary>,
  insurers: Array<{ code: string; name?: string }>,
  paymentNotifErrorsCountByInsurer?: Map<string, number>
): InsurerCobranzaRow[] {
  const rows: InsurerCobranzaRow[] = (insurers || []).map(insurer => {
    const summary = summaries.get(insurer.code) || null;
    const errCount = paymentNotifErrorsCountByInsurer?.get(insurer.code) || 0;
    const stats = deriveCobranzaFromSummary(summary, errCount);
    return {
      insurerCode: insurer.code,
      emitidas: stats.emitidas,
      pagadas: stats.pagadas,
      pagadasConError: stats.pagadasConError,
      tasaCobranza: stats.tasaCobranza
    };
  });

  return rows.sort((a, b) => {
    const aNan = isNaN(a.tasaCobranza);
    const bNan = isNaN(b.tasaCobranza);
    if (aNan && bNan) return 0;
    if (aNan) return 1;
    if (bNan) return -1;
    return b.tasaCobranza - a.tasaCobranza;
  });
}

/** Clasificación de la tasa de cobranza en tiers semánticos. Se usa para
 *  colorear el badge en la UI. */
export type CobranzaTier = 'high' | 'medium' | 'low' | 'na';

export function getCobranzaTier(rate: number): CobranzaTier {
  if (isNaN(rate)) return 'na';
  if (rate >= 80) return 'high';
  if (rate >= 50) return 'medium';
  return 'low';
}
